import type { IContainer, IPhoto, IPhotoCrop } from '../../common/index.js';

const AOI_LENGTH = 7;
const AOI_SOFTMAX_ALPHA = 8;

function getAoiRank(aoi: number[]): number {
    return aoi.map((value) => Math.exp(value * AOI_SOFTMAX_ALPHA)).reduce((sum, value) => sum + value, 0);
}

function getCol(aoi: number[], index: number): number[] {
    const columns = Array.from<number>({ length: AOI_LENGTH });

    for (let i = 0; i < AOI_LENGTH; i++) {
        columns[i] = aoi[(i * AOI_LENGTH) + index];
    }

    return columns;
}

function getRow(aoi: number[], index: number): number[] {
    return aoi.slice(index * AOI_LENGTH, (index * AOI_LENGTH) + AOI_LENGTH);
}

export function getAoiCrop(photo: IPhoto, container: IContainer): IPhotoCrop | null {
    if (photo.aoi == null) {
        return null;
    }

    const aoi = photo.aoi;
    const aoiRowRanks = Array.from({ length: AOI_LENGTH }).map((_, i) => getAoiRank(getRow(aoi, i)));
    const aoiColRanks = Array.from({ length: AOI_LENGTH }).map((_, i) => getAoiRank(getCol(aoi, i)));
    const aoiRowSize = photo.height / AOI_LENGTH;
    const aoiColSize = photo.width / AOI_LENGTH;
    const targetAspectRatio = container.width / container.height;

    const crop = {
        get aspectRatio() {
            return this.width / this.height;
        },
        get width() {
            return this.x2 - this.x1;
        },
        get height() {
            return this.y2 - this.y1;
        },
        get deltaWidth() {
            return Math.abs(crop.width - (crop.height * targetAspectRatio));
        },
        get deltaHeight() {
            return Math.abs(this.height - (this.width / targetAspectRatio));
        },
        get r1() {
            return Math.floor((this.y1 * AOI_LENGTH) / photo.height);
        },
        get r2() {
            return Math.floor((this.y2 * AOI_LENGTH) / photo.height) - 1;
        },
        get c1() {
            return Math.floor((this.x1 * AOI_LENGTH) / photo.width);
        },
        get c2() {
            return Math.floor((this.x2 * AOI_LENGTH) / photo.width) - 1;
        },
        x1: 0,
        x2: photo.width,
        y1: 0,
        y2: photo.height,
    };

    if (crop.aspectRatio > targetAspectRatio) {
        while (crop.aspectRatio > targetAspectRatio) {
            const c1Rank = aoiColRanks[crop.c1] || 0;
            const c2Rank = aoiColRanks[crop.c2] || 0;

            if (c1Rank > c2Rank) {
                crop.x2 -= aoiColSize;
            } else {
                crop.x1 += aoiColSize;
            }
        }

        if (crop.aspectRatio < targetAspectRatio) {
            crop.x1 = Math.max(0, crop.x1 - (crop.deltaWidth / 2));
            crop.x2 = Math.min(photo.width, crop.x2 + crop.deltaWidth);
            crop.x1 = Math.max(0, crop.x1 - crop.deltaWidth);
        }
    } else if (crop.aspectRatio < targetAspectRatio) {
        while (crop.aspectRatio < targetAspectRatio) {
            const r1Rank = aoiRowRanks[crop.r1] || 0;
            const r2Rank = aoiRowRanks[crop.r2] || 0;

            if (r1Rank > r2Rank) {
                crop.y2 -= aoiRowSize;
            } else {
                crop.y1 += aoiRowSize;
            }
        }

        if (crop.aspectRatio > targetAspectRatio) {
            crop.y1 = Math.max(0, crop.y1 - (crop.deltaHeight / 2));
            crop.y2 = Math.min(photo.height, crop.y2 + crop.deltaHeight);
            crop.y1 = Math.max(0, crop.y1 - crop.deltaHeight);
        }
    }

    return {
        x: crop.x1,
        y: crop.y1,
        width: crop.width,
        height: crop.height,
        rank: 1,
    };
}
